import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { useGetPlaylist } from "@/features/playlists/api/get-playlist";
import { NoRecordings } from "@/features/recordings/components/no-recordings";
import { RecordingItem } from "@/features/recordings/components/recording-item";
import { RecordingItemSkeleton } from "@/features/recordings/components/recording-item-skeleton";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { PublicPlaylist } from "@classview/shared-types";

import { ErrorMessage } from "../components/error-message";

export function PlaylistPage() {
  const { id: playlistId } = useParams();
  const { organisation } = useOrganisation();
  const {
    data: playlist,
    isLoading,
    isPlaceholderData,
    isSuccess,
    isError,
    error,
  } = useGetPlaylist(
    {
      organisationId: organisation.id,
      id: playlistId || "",
    },
    {
      placeholderData: () => {
        const availablePlaylists = queryClient.getQueryData<PublicPlaylist[]>([
          "playlists",
          organisation.id,
        ]);
        if (!availablePlaylists) {
          return undefined;
        }

        const playlist = availablePlaylists.find((d) => d.id === playlistId);
        if (!playlist) {
          return undefined;
        }
        return {
          ...playlist,
          recordings: [],
        };
      },
    },
  );

  if (isLoading) {
    return (
      <section>
        <Button variant="ghost" asChild className="mb-5" disabled>
          <Link to="/recordings/playlists">
            <ArrowLeft className="mr-2 size-4" aria-hidden="true" />
            Return to Playlists
          </Link>
        </Button>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <RecordingItemSkeleton />
          <RecordingItemSkeleton />
          <RecordingItemSkeleton />
        </div>
      </section>
    );
  }

  if (isError || !isSuccess) {
    return (
      <section>
        <Button variant="ghost" asChild className="mb-5" disabled>
          <Link to="/recordings/playlists">
            <ArrowLeft className="mr-2 size-4" aria-hidden="true" />
            Return to Playlists
          </Link>
        </Button>
        <ErrorMessage error={error} />
      </section>
    );
  }

  return (
    <section>
      <Button variant="ghost" asChild className="mb-5" disabled>
        <Link to="/recordings/playlists">
          <ArrowLeft className="mr-2 size-4" aria-hidden="true" />
          Return to Playlists
        </Link>
      </Button>
      <Breadcrumb className="mb-4 hidden">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/recordings">Replay</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{playlist?.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <OnboardingHeader title={playlist.name} className="mb-6" />
      {playlist.recordings.length === 0 && !isPlaceholderData && (
        <NoRecordings />
      )}

      {isPlaceholderData && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <RecordingItemSkeleton />
          <RecordingItemSkeleton />
        </div>
      )}

      {!isPlaceholderData && playlist.recordings.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {playlist.recordings.map((recording) => (
            <RecordingItem
              key={recording.id}
              recording={recording}
              playlist={playlist}
            />
          ))}
        </div>
      )}
    </section>
  );
}
